import React, { useEffect, useRef, useState } from 'react';
import API from '../../services/api';
import {
  getEstimatedImpressionsViewId,
  getGranularity,
  getRequestPeriods,
  getSelectedSiteIds,
  getScalarData,
  getTimeSeriesData
} from '../../utils/analytics/estimatedImpressions';
import { getStringfiedField, getWidgetConfig, getWidgetOptions } from '../../utils/widgets';

const useInnerWidgetProperty = (hostRef, selector, property, value) => {
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let frameId;

    const applyProperty = () => {
      const element = host.querySelector(selector);
      if (!element) return false;
      frameId = window.requestAnimationFrame(() => {
        element[property] = value;
      });
      return true;
    };

    if (applyProperty()) {
      return () => window.cancelAnimationFrame(frameId);
    }

    const observer = new MutationObserver(() => {
      if (applyProperty()) observer.disconnect();
    });
    observer.observe(host, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [hostRef, property, selector, value]);
};

const EstimatedImpressionsWidget = ({
  widget,
  filters,
  project,
  lastUpdated,
  handleError
}) => {
  const [data, setData] = useState([]);
  const [previous, setPrevious] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const widgetRef = useRef(null);
  const handleErrorRef = useRef(handleError);
  const viewId = getEstimatedImpressionsViewId(widget);
  const isTraffic = viewId === 'traffic_estimated_impressions';
  const siteIds = getSelectedSiteIds(filters?.selectedSites);
  const siteIdsKey = siteIds.join(',');
  const intervalId = typeof filters?.interval === 'string' ? filters.interval : filters?.interval?.id;
  const requestKey = [
    viewId,
    project?.id,
    filters?.startDate,
    filters?.endDate,
    filters?.timezone,
    intervalId,
    siteIdsKey,
    ...(isTraffic ? [JSON.stringify(filters?.selectedDevices), JSON.stringify(filters?.filterByTime), JSON.stringify(filters?.filterByWeekday)] : []),
    lastUpdated,
    widget.title
  ].join('|');

  useInnerWidgetProperty(widgetRef, 'display-digit', 'previous', previous);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  useEffect(() => {
    if (!viewId || !project?.id || !filters?.startDate || !filters?.endDate || (!isTraffic && siteIds.length === 0)) {
      setData([]);
      setPrevious([]);
      setCategories([]);
      setLoading(false);
      return undefined;
    }

    let mounted = true;
    const granularity = getGranularity(filters.interval);
    const periods = getRequestPeriods(filters, filters.timezone);
    const request = period => ({
      scope: project.isBrandPortal ? { brandPortalId: project.id } : { projectId: project.id },
      period,
      selection: isTraffic ? { deviceIds: filters.selectedDevices?.map(device => typeof device === 'string' ? device : device.id) || [] } : { siteIds },
      ...(isTraffic ? { filters: { weekdays: filters.filterByWeekday || [],
        ...(filters.filterByTime?.start && filters.filterByTime?.end ? { timeOfDay: filters.filterByTime } : {}) } } : {})
    });

    setLoading(true);
    const currentPeriod =
      viewId === 'estimated_impressions_time_series'
        ? { ...periods.current, granularity }
        : periods.current;
    const requests = [API.getAnalyticsViewData(viewId, request(currentPeriod))];

    if (viewId === 'estimated_impressions_digit' || isTraffic) {
      requests.push(API.getAnalyticsViewData(viewId, request(periods.previous)));
    }

    Promise.all(requests)
      .then(([currentResponse, previousResponse]) => {
        if (!mounted) return;

        if (viewId === 'estimated_impressions_digit' || isTraffic) {
          setData(getScalarData(currentResponse, widget.title));
          setPrevious(getScalarData(previousResponse, widget.title));
          setCategories([]);
          return;
        }

        const timeSeries = getTimeSeriesData(currentResponse, granularity, filters.timezone);
        setData(timeSeries.data);
        setCategories(timeSeries.categories);
        setPrevious([]);
      })
      .catch(error => {
        if (!mounted) return;
        setData([]);
        setPrevious([]);
        setCategories([]);
        if (typeof handleErrorRef.current === 'function') {
          handleErrorRef.current({
            detail: {
              code: error?.code,
              message: error?.message || error?.detail || 'Failed to load estimated impressions.'
            }
          });
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [requestKey]);

  const plotOptions =
    viewId === 'estimated_impressions_time_series'
      ? { ...(widget.plotOptions || {}), categories }
      : widget.plotOptions;

  return (
    <widget-container
      ref={widgetRef}
      key={`analytics-${widget.id}`}
      id={widget.id}
      info={getStringfiedField(widget, 'info')}
      format={widget.format}
      unit={widget.unit}
      plot-options={getStringfiedField({ plotOptions }, 'plotOptions')}
      widget-title={widget.title}
      data={JSON.stringify(data)}
      display={widget.display}
      config={getWidgetConfig(widget.config, project.companyId)}
      options={getWidgetOptions(widget)}
      is-loading={loading}
      y-title={widget.yTitle}
      y-title-secondary={widget.yTitleSecondary}
    />
  );
};

export default EstimatedImpressionsWidget;
